import { ReactNode, RefObject, useRef } from 'react';

import { ElementDimensions, useElementDimensions } from '@hooks/useElementDimensions';
import { useScrollState } from '@hooks/useScrollState';
import { PageIndicator } from '../components/PageIndicator';
import { getTotalPages } from '../ContinuousCarousel/helpers/getTotalPages';

/*
  This carousel is designed to be used where we have icons instead of cards, where we do not see the edge of
  the next card and so we need the dots underneath to give a clear indication that this is indeed a carousel
  and has further items you can swipe to. An example of this is the BragBar.

  Do not use this carousel on a desktop screen - there are no buttons!
*/

interface IProps {
  children: ReactNode;
  id: string;
  className?: string;
  smallIndicatorSpacing?: boolean;
}

function getCurrentPage(scrollPosition: number, dimensions: ElementDimensions | null): number {
  return dimensions
    ? Math.ceil((scrollPosition + dimensions.clientWidth) / dimensions.clientWidth)
    : 1;
}

const scrollToPage = (page: number, containerRef: RefObject<HTMLDivElement | null>): void => {
  const { current } = containerRef;
  if (current) {
    const pageWidth = current.clientWidth;
    current.scrollTo({ left: (page - 1) * pageWidth, behavior: 'smooth' });
  }
};

export const ContinuousCarouselDots = ({
  children,
  id,
  className = '',
  smallIndicatorSpacing = false,
}: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollPosition } = useScrollState(containerRef);
  // We pass the children as deps so that the hook re-measures any time the children change.
  const dimensions = useElementDimensions(containerRef, [children]);
  const totalPages = getTotalPages(dimensions);
  const currentPage = getCurrentPage(scrollPosition, dimensions);

  return (
    <div className={`${className} flex flex-col`}>
      <div
        ref={containerRef}
        className="carousel-container scrollbar-none flex snap-x overflow-y-hidden scroll-smooth"
      >
        {children}
      </div>

      <PageIndicator
        currentPage={currentPage}
        totalPages={totalPages}
        onClick={page => {
          scrollToPage(page, containerRef);
        }}
        id={id}
        smallIndicatorSpacing={smallIndicatorSpacing}
      />
    </div>
  );
};
